const DEMO_CLIPS = [
  // Add Loom embed URLs here as clips are ready:
  // { title: 'Using compare to review improvement proof', summary: 'Find improved, regressed, and low-data changes across scopes.', embedUrl: 'https://www.loom.com/embed/...' },
];

function DemoClipsSection() {
  const clips = DEMO_CLIPS.filter((clip) => clip.embedUrl);

  if (!clips.length) {
    return null;
  }

  return (
    <section className="overview-band">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Demo clips</p>
          <h3>Watch the core workflows</h3>
        </div>
      </div>
      <div className="demo-clip-grid">
        {clips.map((clip) => (
          <article className="detail-card demo-clip-card" key={clip.embedUrl}>
            <div>
              <strong>{clip.title}</strong>
              <p>{clip.summary}</p>
            </div>
            <div className="demo-clip-frame">
              <iframe
                allowFullScreen
                src={clip.embedUrl}
                title={clip.title}
              />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function HowRtiWorksView() {
  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel how-rti-works-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Guide</p>
            <h2>How Runtime Reliability Intelligence works</h2>
            <p className="panel-copy how-rti-works-intro">
              Agentic Blue is a reliability intelligence and validation layer for agentic AI systems. It sits above customer systems, analyses runtime behaviour, and presents evidence-backed findings, recommendations, and improvement proof.
            </p>
          </div>
        </div>

        <div className="how-rti-works-pillars">
          <section className="detail-card how-rti-works-pillar">
            <span className="summary-card-label">What RTI analyzes</span>
            <strong className="how-rti-works-pillar-title">Runtime-derived system behavior</strong>
            <p>RTI works from persisted runs, steps, retries, retrieval evidence, and dependency activity rather than static code inspection alone.</p>
          </section>
          <section className="detail-card how-rti-works-pillar">
            <span className="summary-card-label">What RTI produces</span>
            <strong className="how-rti-works-pillar-title">Signals, findings, and proof</strong>
            <p>It calculates bounded signals, raises evidence-backed findings, generates targeted validation, and shows whether fixes improved behavior.</p>
          </section>
          <section className="detail-card how-rti-works-pillar">
            <span className="summary-card-label">How reliability is judged</span>
            <strong className="how-rti-works-pillar-title">Workflows first, components for localization</strong>
            <p>Workflows are the primary unit of reliability judgment. Components and architecture explain where problems are concentrating.</p>
          </section>
          <section className="detail-card how-rti-works-pillar how-rti-works-pillar-muted">
            <span className="summary-card-label">How validation is handled</span>
            <strong className="how-rti-works-pillar-title">Prepared is not the same as proven</strong>
            <p>Preparedness, validation coverage, and improvement proof are kept separate so reliability claims stay honest.</p>
          </section>
        </div>

        <section className="overview-band how-rti-get-started-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Get started</p>
              <h3>A practical review loop</h3>
            </div>
          </div>
          <div className="how-rti-get-started-grid">
            <section className="detail-card how-rti-get-started-step">
              <span>1</span>
              <strong>Upload runtime traces</strong>
              <p>Add representative traces for the workflow you want to inspect.</p>
            </section>
            <section className="detail-card how-rti-get-started-step">
              <span>2</span>
              <strong>Review candidate findings</strong>
              <p>Use Findings to scan what surfaced, inspect impact, and decide which items are worth a deeper review.</p>
            </section>
            <section className="detail-card how-rti-get-started-step">
              <span>3</span>
              <strong>Validate the evidence</strong>
              <p>Open finding detail, inspect supporting traces, and create or attach targeted validation when the finding supports it.</p>
            </section>
            <section className="detail-card how-rti-get-started-step">
              <span>4</span>
              <strong>Choose what to improve</strong>
              <p>Start improvement tracking for the finding or workflow once the team agrees the behavior should change.</p>
            </section>
            <section className="detail-card how-rti-get-started-step">
              <span>5</span>
              <strong>Upload post-fix traces</strong>
              <p>After the workflow changes, add follow-up traces from the same workflow lineage.</p>
            </section>
            <section className="detail-card how-rti-get-started-step">
              <span>6</span>
              <strong>Compare the effect</strong>
              <p>Use Compare to see what improved, regressed, stayed flat, or still lacks enough evidence.</p>
            </section>
          </div>
        </section>

        <section className="overview-band how-rti-roadmap-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Flow</p>
              <h3>From traces to action</h3>
            </div>
          </div>
          <div className="how-rti-roadmap">
            <section className="detail-card how-rti-roadmap-step">
              <span className="how-rti-roadmap-marker">1</span>
              <strong>Build a runtime-derived view</strong>
              <p>RTI connects to runtime evidence, constructs a canonical view of the system, and preserves the context needed to inspect conclusions later.</p>
            </section>
            <section className="detail-card how-rti-roadmap-step">
              <span className="how-rti-roadmap-marker">2</span>
              <strong>Calculate bounded signals</strong>
              <p>Analysis turns traces into inspectable measurements such as failure rates, weak evidence rates, validation gaps, and retrieval precision issues.</p>
            </section>
            <section className="detail-card how-rti-roadmap-step">
              <span className="how-rti-roadmap-marker">3</span>
              <strong>Raise findings and recommendations</strong>
              <p>Signals are synthesized into findings, linked to workflows and components when possible, and paired with remediation and validation follow-up.</p>
            </section>
          </div>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Compare</p>
              <h3>How improvement proof works</h3>
            </div>
          </div>
          <div className="detail-summary-grid">
            <section className="detail-card">
              <strong>One proof workspace</strong>
              <p>Compare reviews before-and-after evidence for findings, workflows, and components from one system-level workspace.</p>
            </section>
            <section className="detail-card">
              <strong>Direction, not guesswork</strong>
              <p>Each comparison is labeled improved, regressed, stable, or low data so sparse evidence does not get overstated.</p>
            </section>
            <section className="detail-card">
              <strong>Validation stays separate</strong>
              <p>Workflow compare can show validation proof when labeled pack results exist, while runtime movement and validation coverage remain distinct.</p>
            </section>
          </div>
          <div className="detail-card compare-guide-card">
            <div className="chapter-inline-header">
              <strong>What compare uses</strong>
            </div>
            <div className="compare-guide-grid">
              <div>
                <span className="summary-card-label">Findings</span>
                <p>Expected movement, baseline/current readings, sample sizes, analysis versions, and historical baseline status when available.</p>
              </div>
              <div>
                <span className="summary-card-label">Workflows</span>
                <p>Current versus previous completed analyses, metric deltas, reliability verdicts, and validation proof when pack results exist.</p>
              </div>
              <div>
                <span className="summary-card-label">Components</span>
                <p>Persisted component health signals, linked-finding movement, run counts, and component-level metric deltas.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Cost framing</p>
              <h3>How cost claims stay bounded</h3>
            </div>
          </div>
          <div className="detail-summary-grid">
            <section className="detail-card">
              <strong>Observed units first</strong>
              <p>Agentic Blue starts with evidence it can observe directly, such as tokens per run, extra loop steps, screening calls, latency, retries, or external-step risk.</p>
            </section>
            <section className="detail-card">
              <strong>Money needs explicit context</strong>
              <p>Finding and compare surfaces can estimate token cost, period spend, or business cost only when that context is supplied in import or provenance metadata.</p>
            </section>
            <section className="detail-card">
              <strong>Recovery needs comparison</strong>
              <p>Observed recovery is shown only when comparable before-and-after evidence shows a lower-is-better cost or workflow-unit metric improved.</p>
            </section>
          </div>
          <div className="detail-card cost-claim-guide-card">
            <div className="cost-claim-guide-header">
              <span className="summary-card-label">Claim ladder</span>
              <strong>From workflow units to stronger estimates</strong>
            </div>
            <div className="cost-claim-guide-grid">
              <div>
                <span>Workflow units only</span>
                <p>Current exposure in observed units, not money.</p>
              </div>
              <div>
                <span>Pricing supplied</span>
                <p>Estimated token cost per run when model pricing exists.</p>
              </div>
              <div>
                <span>Pricing and volume supplied</span>
                <p>Estimated period spend when run/request volume is also present.</p>
              </div>
              <div>
                <span>Business context supplied</span>
                <p>Business-cost framing only from partner-provided value context.</p>
              </div>
            </div>
            <p className="overview-note">
              Stronger estimates can appear on finding and compare surfaces when explicit inputs exist. Overview stays conservative in this milestone: observed exposure and recovery only, not dollars, monthly savings, ROI, payback, or business-value totals.
            </p>
          </div>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Interpretation</p>
              <h3>How to read the output</h3>
            </div>
          </div>
          <div className="detail-summary-grid">
            <section className="detail-card">
              <strong>Runtime summary</strong>
              <p>Overview summarizes current runtime reliability, why the product is making that claim, and where the most important risk is concentrated.</p>
            </section>
            <section className="detail-card">
              <strong>Confidence</strong>
              <p>Confidence reflects the strength and freshness of the available runtime evidence. Lower confidence means the claim should be treated more cautiously.</p>
            </section>
            <section className="detail-card">
              <strong>Validation</strong>
              <p>A prepared pack means validation work can be run. It does not by itself establish coverage or prove that a fix improved the workflow.</p>
            </section>
          </div>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Views</p>
              <h3>How the main pages work together</h3>
            </div>
          </div>
          <div className="stack compact-stack">
            <section className="detail-card">
              <strong>Overview</strong>
              <p>Start here to understand the current claim, why the claim exists, and which route to open next.</p>
            </section>
            <section className="detail-card">
              <strong>Findings</strong>
              <p>Use findings as the main triage surface for what matters, why it matters, and what should change.</p>
            </section>
            <section className="detail-card">
              <strong>Workflows and system map</strong>
              <p>Use workflows to understand end-to-end behavior and use architecture or components to localize where issues are accumulating.</p>
            </section>
            <section className="detail-card">
              <strong>Validation and compare</strong>
              <p>Use validation to see what is prepared versus what is covered, and use compare to review runtime movement, validation proof, and low-data caveats.</p>
            </section>
          </div>
        </section>

        <DemoClipsSection />
      </div>
    </section>
  );
}
